// Typed dashboard API client. One function per endpoint in contracts/dashboard-api.md.
//
// READ-ONLY: the only mutating calls are alert ack/resolve, which act on server-side alert
// rows exclusively — there is no endpoint here that could open, modify, or close a trade, or
// send anything to a terminal. The two writes carry the CSRF token.

import type {
  AccountDecommissionResponse,
  AccountDetailResponse,
  AccountsSnapshot,
  AckResponse,
  AgentOkResponse,
  AgentSecretResponse,
  AgentsListResponse,
  AlertsSnapshot,
  AlertState,
  AlertSeverity,
  AuthUser,
  DealsResponse,
  EventsResponse,
  FleetSummary,
  InviteCreateResponse,
  InviteOkResponse,
  InviteRole,
  InvitesListResponse,
  LoginResponse,
  MeResponse,
  MetricsResponse,
  MfaEnrollResponse,
  MfaOkResponse,
  OrdersResponse,
  PositionsResponse,
  RegisterCheckResponse,
  RegisterResult,
  TerminalsResponse,
  TradingVpsRemoveResponse,
  TradingVpsResponse,
  VpsDecommissionResponse,
  VpsResponse,
} from '../types/api';
import { del, get, post, setCsrfToken } from './http';

// ---- Auth -----------------------------------------------------------------------------

export async function login(email: string, password: string, totp?: string): Promise<LoginResponse> {
  const body: Record<string, string> = { email, password };
  if (totp) body.totp = totp;
  const res = await post<LoginResponse>('/auth/login', body);
  setCsrfToken(res.csrf_token);
  return res;
}

export async function logout(): Promise<void> {
  await post<{ ok?: boolean }>('/auth/logout', {}, { csrf: true });
}

export async function me(signal?: AbortSignal): Promise<AuthUser> {
  const res = await get<MeResponse>('/auth/me', signal);
  setCsrfToken(res.csrf_token);
  return res.user;
}

// ---- MFA self-service enrollment ------------------------------------------------------
// All three are CSRF-protected mutations on the caller's own user row (they never touch a
// terminal or a trade). Enroll stores a pending secret; confirm activates it; disable clears
// it — each requires a valid current TOTP code except enroll.

/** Begin enrollment: returns a pending base32 secret + otpauth URI (does NOT enable MFA). */
export function mfaEnroll(): Promise<MfaEnrollResponse> {
  return post<MfaEnrollResponse>('/auth/mfa/enroll', {}, { csrf: true });
}

/** Confirm enrollment with a 6-digit code; enables MFA on success. */
export function mfaConfirm(totp: string): Promise<MfaOkResponse> {
  return post<MfaOkResponse>('/auth/mfa/confirm', { totp }, { csrf: true });
}

/** Disable MFA; requires a valid current 6-digit code. */
export function mfaDisable(totp: string): Promise<MfaOkResponse> {
  return post<MfaOkResponse>('/auth/mfa/disable', { totp }, { csrf: true });
}

// ---- Snapshots (initial load + resync) ------------------------------------------------

export function fleetSummary(signal?: AbortSignal): Promise<FleetSummary> {
  return get<FleetSummary>('/fleet/summary', signal);
}

export function accounts(signal?: AbortSignal): Promise<AccountsSnapshot> {
  return get<AccountsSnapshot>('/accounts', signal);
}

export function alerts(
  params: { state?: AlertState | ''; sev?: AlertSeverity | '' } = {},
  signal?: AbortSignal,
): Promise<AlertsSnapshot> {
  const q = new URLSearchParams();
  if (params.state) q.set('state', params.state);
  if (params.sev) q.set('sev', params.sev);
  const qs = q.toString();
  return get<AlertsSnapshot>('/alerts' + (qs ? '?' + qs : ''), signal);
}

// ---- Account drill-down ---------------------------------------------------------------

export function accountDetail(id: string, signal?: AbortSignal): Promise<AccountDetailResponse> {
  return get<AccountDetailResponse>('/accounts/' + encodeURIComponent(id), signal);
}

export function accountPositions(id: string, signal?: AbortSignal): Promise<PositionsResponse> {
  return get<PositionsResponse>('/accounts/' + encodeURIComponent(id) + '/positions', signal);
}

export function accountOrders(id: string, signal?: AbortSignal): Promise<OrdersResponse> {
  return get<OrdersResponse>('/accounts/' + encodeURIComponent(id) + '/orders', signal);
}

export function accountDeals(
  id: string,
  params: { from?: string; to?: string; limit?: number; cursor?: string } = {},
  signal?: AbortSignal,
): Promise<DealsResponse> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return get<DealsResponse>('/accounts/' + encodeURIComponent(id) + '/deals' + (qs ? '?' + qs : ''), signal);
}

export function accountEvents(id: string, limit = 50, signal?: AbortSignal): Promise<EventsResponse> {
  return get<EventsResponse>('/accounts/' + encodeURIComponent(id) + '/events?limit=' + limit, signal);
}

export function accountMetrics(
  id: string,
  params: { from?: string; to?: string; res?: 'raw' | '1m' | '5m' | '1h' } = {},
  signal?: AbortSignal,
): Promise<MetricsResponse> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  if (params.res) q.set('res', params.res);
  const qs = q.toString();
  return get<MetricsResponse>('/accounts/' + encodeURIComponent(id) + '/metrics' + (qs ? '?' + qs : ''), signal);
}

// ---- Infra ----------------------------------------------------------------------------

export function vps(signal?: AbortSignal): Promise<VpsResponse> {
  return get<VpsResponse>('/vps', signal);
}

export function terminals(signal?: AbortSignal): Promise<TerminalsResponse> {
  return get<TerminalsResponse>('/terminals', signal);
}

export function tradingVps(signal?: AbortSignal): Promise<TradingVpsResponse> {
  return get<TradingVpsResponse>('/trading-vps', signal);
}

/**
 * Remove a retired guard host from direct VPS monitoring (admin-only, CSRF-protected,
 * audit-logged server-side as `trading_vps_removed`). Deletes only the guard's own health
 * rows — never a terminal command or a trade. A guard that is still running simply
 * re-registers on its next heartbeat.
 */
export function removeTradingVps(vpsId: string): Promise<TradingVpsRemoveResponse> {
  return del<TradingVpsRemoveResponse>('/trading-vps/' + encodeURIComponent(vpsId), { csrf: true });
}

// ---- Decommission (admin-only) --------------------------------------------------------
// Remove a registration the operator no longer runs (e.g. a retired test terminal). These are
// the only DELETE calls that erase fleet history — never a terminal command or a trade. Each is
// CSRF-protected and audit-logged server-side, and runs in one transaction so no partial delete.

/** Decommission a whole VPS: its terminals, accounts, history and alerts. */
export function decommissionVps(vpsId: string): Promise<VpsDecommissionResponse> {
  return del<VpsDecommissionResponse>('/vps/' + encodeURIComponent(vpsId), { csrf: true });
}

/** Decommission a single account_source and all its dependent rows (terminal/VPS untouched). */
export function decommissionAccount(id: string): Promise<AccountDecommissionResponse> {
  return del<AccountDecommissionResponse>('/accounts/' + encodeURIComponent(id), { csrf: true });
}

// ---- Alert workflow (server-side rows only) -------------------------------------------

/** Acknowledge an alert. CSRF-protected; audit-logged server-side. */
export function ackAlert(id: string): Promise<AckResponse> {
  return post<AckResponse>('/alerts/' + encodeURIComponent(id) + '/ack', {}, { csrf: true });
}

/**
 * Resolve an alert. Mirrors the ack contract (CSRF, operator+, returns {ok, alert}); the
 * design spec's read-only guarantee explicitly covers "ack/resolve act only on server-side
 * alert rows", and the alert lifecycle ends in `resolved`.
 */
export function resolveAlert(id: string): Promise<AckResponse> {
  return post<AckResponse>('/alerts/' + encodeURIComponent(id) + '/resolve', {}, { csrf: true });
}

// ---- Agent provisioning (admin-only) --------------------------------------------------
// These mint / rotate / revoke / delete HMAC ingestion credentials for a VPS collector. They
// never touch a terminal or a trade; they manage server-side agent rows only. Every mutation
// is CSRF-protected and audit-logged server-side. create/rotate return the plaintext secret
// exactly ONCE — the caller must surface it immediately and never persist it.

/** List provisioned agents (metadata only — never a secret). */
export function listAgents(signal?: AbortSignal): Promise<AgentsListResponse> {
  return get<AgentsListResponse>('/agents', signal);
}

/** Create an agent for a VPS. Returns { agent_id, secret } shown ONCE. */
export function createAgent(vps_label: string): Promise<AgentSecretResponse> {
  return post<AgentSecretResponse>('/agents', { vps_label }, { csrf: true });
}

/** Rotate an agent's secret. Returns the NEW secret ONCE; the old one keeps working briefly. */
export function rotateAgent(id: string): Promise<AgentSecretResponse> {
  return post<AgentSecretResponse>('/agents/' + encodeURIComponent(id) + '/rotate', {}, { csrf: true });
}

/** Revoke an agent — ingestion is refused (403) for it going forward. */
export function revokeAgent(id: string): Promise<AgentOkResponse> {
  return post<AgentOkResponse>('/agents/' + encodeURIComponent(id) + '/revoke', {}, { csrf: true });
}

/** Delete an agent row entirely. */
export function deleteAgent(id: string): Promise<AgentOkResponse> {
  return del<AgentOkResponse>('/agents/' + encodeURIComponent(id), { csrf: true });
}

// ---- Registration invites (admin-only) ------------------------------------------------
// An admin mints a single-use, time-limited invite; the create response carries the raw
// token + one-time URL EXACTLY ONCE (never retrievable again). Listing never returns token
// material. Every mutation is CSRF-protected and audit-logged server-side. These manage
// server-side invite rows only — they never touch a terminal or a trade.

/** List pending (unused, unexpired) invites — metadata only, never a token. */
export function listInvites(signal?: AbortSignal): Promise<InvitesListResponse> {
  return get<InvitesListResponse>('/invites', signal);
}

/** Create a single-use invite. Returns { url, token } shown ONCE. */
export function createInvite(input: {
  role: InviteRole;
  email?: string;
  expires_in_hours?: number;
}): Promise<InviteCreateResponse> {
  const body: Record<string, unknown> = { role: input.role };
  if (input.email) body.email = input.email;
  if (input.expires_in_hours != null) body.expires_in_hours = input.expires_in_hours;
  return post<InviteCreateResponse>('/invites', body, { csrf: true });
}

/** Revoke (delete) a pending invite. */
export function deleteInvite(id: string): Promise<InviteOkResponse> {
  return del<InviteOkResponse>('/invites/' + encodeURIComponent(id), { csrf: true });
}

// ---- Public self-service registration (NO session) ------------------------------------
// These are the only unauthenticated calls in the client. They carry no CSRF token (there is
// no session yet) and are reached from the public /register page.

/** Validate an invite token without consuming it. Rejects 404 (unknown) / 410 (used/expired). */
export function checkInvite(token: string, signal?: AbortSignal): Promise<RegisterCheckResponse> {
  return get<RegisterCheckResponse>('/register/' + encodeURIComponent(token), signal);
}

/** Redeem an invite: set the account's own email + password. Creates the user server-side. */
export function register(input: { token: string; email: string; password: string }): Promise<RegisterResult> {
  return post<RegisterResult>('/register', input);
}
